import { useEffect, useState } from 'react';
import { supabase, type Profile } from '../lib/supabase';

export interface UseProfileReturn {
  profile: Profile | null;
  isPro: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
}

function checkIsPro(profile: Profile | null): boolean {
  if (!profile?.pro_until) return false;
  return new Date(profile.pro_until) > new Date();
}

export function useProfile(userId: string | undefined): UseProfileReturn {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async () => {
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('[Atilum] Error fetching profile:', error);
    }

    setProfile(data ?? null);
    setLoading(false);
  };

  useEffect(() => {
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchProfile();

    // Subscribe to real-time changes on this user's profile row
    // When the Paddle webhook updates pro_until, the browser updates instantly
    const channel = supabase
      .channel(`profile:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          const updated = payload.new as Profile;
          setProfile(updated);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  return {
    profile,
    isPro: checkIsPro(profile),
    loading,
    refetch: fetchProfile,
  };
}
